<!-- src/components/Kitchen/Recipes/RecipeDetail.vue -->
<template>
  <div class="recipe-page bg-primary main">

    <!-- Top nav -->
    <div class="recipe-topbar rowSpaceBetween fullWidth">
      <TopDeck
        firstElement="Détail"
        secondElement="recette"
        :coloredSecond="true"
        actionHeaderIcon="mdi-arrow-left"
        :actionheaderFunction="() => $router.back()"
        actionHeaderColor="accent"
        actionHeaderTextColor="white"
      />
      <div class="rowCustom">
        <ThemeToggle />
        <AccountToggle />
      </div>
    </div>

    <!-- ═══ CONTENU CENTRÉ ═══ -->
    <div class="recipe-centered">

      <!-- Eyebrow + titre -->
      <div class="hero-eyebrow">
        <span class="hero-eyebrow-text">Recette</span>
      </div>
      <h1 class="hero-title">{{ recipe?.title || 'Sans titre' }}</h1>

      <!-- ── HERO : image + macros côte à côte ── -->
      <div class="hero-layout">

        <!-- Colonne gauche : image + tags -->
        <div class="hero-left">
          <div class="hero-img-frame">
            <v-img :src="previewSrc" cover class="hero-img" @error="onPreviewError">
              <template #placeholder>
                <div class="fill-height d-flex align-center justify-center">
                  <v-progress-circular indeterminate color="accent" size="28" />
                </div>
              </template>
            </v-img>
          </div>

          <!-- Tags sous l'image -->
          <div class="hero-tags">
            <span v-if="recipe?.category" class="tag-pill tag-pill--accent">{{ $t(recipe.category) }}</span>
            <span v-if="recipe?.diet" class="tag-pill">{{ recipe.diet }}</span>
            <span v-if="recipe?.meatConsumption" class="tag-pill">{{ recipe.meatConsumption }}</span>
            <template v-if="recipe?.categories?.length">
              <span
                v-for="cat in recipe.categories.filter(c => c !== recipe.category)"
                :key="cat"
                class="tag-pill"
              >{{ $t(cat) }}</span>
            </template>
          </div>
        </div>

        <!-- Colonne droite : macros -->
        <div class="hero-right">
          <!-- Calories + portions en tête -->
          <div class="macro-header">
            <div class="macro-kcal">
              <span class="macro-kcal__val">{{ recipe?.totals?.kcal ?? 0 }}</span>
              <span class="macro-kcal__unit">kcal</span>
            </div>
            <div v-if="recipe?.servings" class="macro-portions">
              <v-icon size="14" color="accent" class="mr-1">mdi-account-multiple-outline</v-icon>
              <span>{{ recipe.servings }} portion{{ recipe.servings > 1 ? 's' : '' }}</span>
            </div>
          </div>

          <!-- Barres macros -->
          <div class="macro-bars">
            <div class="macro-bar-item">
              <div class="macro-bar-row">
                <span class="macro-bar-label">Protéines</span>
                <span class="macro-bar-val">{{ format1(recipe?.totals?.protein) }}&thinsp;g</span>
              </div>
              <div class="macro-bar-track">
                <div class="macro-bar-fill macro-bar-fill--protein" :style="{ width: macroBarWidth('protein') }" />
              </div>
            </div>
            <div class="macro-bar-item">
              <div class="macro-bar-row">
                <span class="macro-bar-label">Glucides</span>
                <span class="macro-bar-val">{{ format1(recipe?.totals?.carbs) }}&thinsp;g</span>
              </div>
              <div class="macro-bar-track">
                <div class="macro-bar-fill macro-bar-fill--carbs" :style="{ width: macroBarWidth('carbs') }" />
              </div>
            </div>
            <div class="macro-bar-item">
              <div class="macro-bar-row">
                <span class="macro-bar-label">Lipides</span>
                <span class="macro-bar-val">{{ format1(recipe?.totals?.fat) }}&thinsp;g</span>
              </div>
              <div class="macro-bar-track">
                <div class="macro-bar-fill macro-bar-fill--fat" :style="{ width: macroBarWidth('fat') }" />
              </div>
            </div>
          </div>

          <!-- kcal / portion -->
          <div v-if="recipe?.servings && recipe.servings > 0" class="macro-per-portion">
            <v-icon size="13" color="accent" class="mr-1">mdi-silverware-fork-knife</v-icon>
            <span>{{ Math.round((recipe?.totals?.kcal ?? 0) / recipe.servings) }} kcal / portion</span>
          </div>

          <!-- Nova warning -->
          <div v-if="novaWarning" class="recipe-warning" :class="'recipe-warning--' + novaWarning.type">
            <v-icon size="14" class="mr-1">{{ novaWarning.icon }}</v-icon>
            <span>{{ novaWarning.message }}</span>
          </div>

          <!-- Actions -->
          <div class="hero-actions">
            <button class="action-btn action-btn--cart" @click="createShoppingListFromRecipe()">
              <v-icon size="15" class="mr-1">mdi-cart-plus</v-icon>
              Liste de courses
            </button>
            <div class="action-icons">
              <button
                v-if="!recipe?.baseRecipe && !isFromBaseCatalog"
                class="hero-btn hero-btn--icon"
                :class="{ 'hero-btn--icon-active': recipe?.favorite }"
                @click="toggleFavorite()"
                title="Favori"
              >
                <v-icon size="17">{{ recipe?.favorite ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
              </button>
              <button v-if="canEditBaseRecipe" class="hero-btn hero-btn--icon" @click="editRecipe()" title="Modifier">
                <v-icon size="17">mdi-pencil-outline</v-icon>
              </button>
              <button v-if="canEditBaseRecipe" class="hero-btn hero-btn--icon hero-btn--danger" @click="requestDelete" title="Supprimer">
                <v-icon size="17">mdi-delete-outline</v-icon>
              </button>
            </div>
          </div>
        </div>
      </div><!-- /hero-layout -->

      <!-- ── INGRÉDIENTS ── -->
      <div class="section-block">
        <p class="section-title">
          Ingrédients
          <span v-if="recipe?.ingredients?.length" class="section-count">{{ recipe.ingredients.length }}</span>
        </p>

        <div v-if="!recipe?.ingredients?.length" class="recipe-empty">
          <v-icon size="28" style="opacity:.3">mdi-food-off</v-icon>
          <span>Aucun ingrédient</span>
        </div>

        <!-- Grille bulles ingrédients -->
        <div v-else class="ing-bubbles">
          <div
            v-for="(ing, i) in recipe.ingredients"
            :key="i"
            class="ing-bubble"
            :class="{ 'ing-bubble--clickable': ing.productImage || ing.availableStores?.length || ing.productLink }"
            @click="openIngredientDetail(ing)"
          >
            <div class="ing-bubble__circle">
              <img v-if="ing.productImage" :src="ing.productImage" class="ing-bubble__img" :alt="ing.name" />
              <div v-else class="ing-bubble__placeholder">
                <v-icon size="26" style="opacity:.3">mdi-food-variant</v-icon>
              </div>
              <div class="ing-card__badges">
                <span
                  v-if="getNovaDisplay(ing.novaGroup)"
                  class="ing-micro-badge"
                  :style="{ background: getNovaDisplay(ing.novaGroup).color }"
                >N{{ getNovaNumber(ing.novaGroup) }}</span>
                <span
                  v-if="ing.nutriscore"
                  class="ing-micro-badge"
                  :style="{ background: nutriscoreBg(ing.nutriscore) }"
                >{{ ing.nutriscore }}</span>
              </div>
              <!-- Crayon admin -->
              <button
                v-if="isAdmin"
                class="ing-bubble__edit"
                @click.stop="openIngredientEdit(ing)"
                title="Modifier l'ingrédient"
              >
                <v-icon size="12">mdi-pencil</v-icon>
              </button>
            </div>
            <span class="ing-bubble__name">{{ ing.name || ing.productName }}</span>
            <span class="ing-bubble__qty">{{ ing.grams }}&thinsp;g</span>
          </div>
        </div>
      </div>

      <!-- ── PRÉPARATION (Étapes) ── -->
      <div v-if="recipe?.steps?.length" class="section-block">
        <p class="section-title">Préparation</p>
        <div class="steps-list">
          <div v-for="(step, i) in recipe.steps" :key="i" class="step-item">
            <div class="step-number">{{ i + 1 }}</div>
            <div class="step-content detail-text" v-html="step" />
          </div>
        </div>
      </div>

      <!-- ── NOTES ── -->
      <div v-if="recipe?.notes" class="section-block">
        <p class="section-title">Préparation</p>
        <div class="recipe-notes detail-text" v-html="recipe.notes" />
      </div>

    </div><!-- /recipe-centered -->

    <!-- ─── Dialog suppression ─── -->
    <v-dialog v-model="askDelete" max-width="400">
      <v-card rounded="xl" color="secondary">
        <v-card-title class="pt-5 px-5 text-body-1 font-weight-bold detail-text">Supprimer la recette ?</v-card-title>
        <v-card-text class="px-5 text-body-2 detail-text">Cette action est définitive et irréversible.</v-card-text>
        <v-card-actions class="px-5 pb-5">
          <v-spacer />
          <button class="dialog-btn dialog-btn--cancel" @click="askDelete = false">Annuler</button>
          <button class="dialog-btn dialog-btn--delete" @click="deleteRecipe">Supprimer</button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── Dialog détail ingrédient ─── -->
    <v-dialog v-model="ingredientDetailDialog.open" max-width="500" scrollable>
      <v-card v-if="ingredientDetailDialog.ing" rounded="xl" color="secondary">
        <div class="ing-detail-header">
          <div class="ing-detail-img-wrap">
            <img v-if="ingredientDetailDialog.ing.productImage" :src="ingredientDetailDialog.ing.productImage" class="ing-detail-img" />
            <div v-else class="ing-detail-img-placeholder">
              <v-icon size="28" style="opacity:.35">mdi-food-variant</v-icon>
            </div>
          </div>
          <div class="ing-detail-info">
            <p class="ing-detail-name">{{ ingredientDetailDialog.ing.name || ingredientDetailDialog.ing.productName }}</p>
            <p v-if="ingredientDetailDialog.ing.productBrand" class="ing-detail-brand">{{ ingredientDetailDialog.ing.productBrand }}</p>
            <div class="ing-detail-badges">
              <span v-if="getNovaDisplay(ingredientDetailDialog.ing.novaGroup)" class="ing-micro-badge" :style="{ background: getNovaDisplay(ingredientDetailDialog.ing.novaGroup).color }">
                Nova {{ getNovaNumber(ingredientDetailDialog.ing.novaGroup) }}
              </span>
              <span v-if="ingredientDetailDialog.ing.nutriscore" class="ing-micro-badge" :style="{ background: nutriscoreBg(ingredientDetailDialog.ing.nutriscore) }">
                {{ ingredientDetailDialog.ing.nutriscore }}
              </span>
            </div>
          </div>
          <button class="ing-detail-close" @click="ingredientDetailDialog.open = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <v-card-text class="px-5 pt-3 pb-5">
          <a v-if="ingredientDetailDialog.ing.productLink" :href="ingredientDetailDialog.ing.productLink" target="_blank" rel="noopener noreferrer" class="ing-product-link">
            <v-icon size="13" class="mr-1">mdi-open-in-new</v-icon>
            Voir la page produit
          </a>
          <div v-if="ingredientDetailDialog.ing.nutriments_map?.length" class="mt-4">
            <p class="section-title mb-2" style="font-size:11px">Valeurs / 100 g</p>
            <div class="nutr-table">
              <div v-for="n in ingredientDetailDialog.ing.nutriments_map" :key="n.key" class="nutr-table-row">
                <span class="detail-text">{{ nutrimentLabel(n.key) }}</span>
                <strong class="detail-text">{{ n.value }}</strong>
              </div>
            </div>
          </div>
          <div v-if="ingredientDetailDialog.ing.availableStores?.length" class="mt-4">
            <p class="section-title mb-2" style="font-size:11px">Disponible dans</p>
            <div class="d-flex flex-wrap gap-2">
              <span v-for="store in ingredientDetailDialog.ing.availableStores" :key="store" class="store-pill">
                <v-icon size="12" class="mr-1">mdi-store-outline</v-icon>{{ store }}
              </span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ─── Dialog édition ingrédient (admin) ─── -->
    <v-dialog v-model="ingEditDialog" max-width="540" scrollable>
      <v-card rounded="xl" color="secondary">

        <!-- Header -->
        <div class="ied-header">
          <div class="ied-header__info">
            <p class="ied-header__label">Modifier l'ingrédient</p>
            <p class="ied-header__name">{{ ingEditForm.productName || 'Sans nom' }}</p>
          </div>
          <button class="ing-detail-close" @click="ingEditDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <v-card-text class="ied-body">

          <!-- Aperçu image -->
          <div class="ied-img-preview">
            <div class="ied-img-wrap">
              <img v-if="ingEditForm.productImage" :src="ingEditForm.productImage" class="ied-img" />
              <div v-else class="ied-img-placeholder">
                <v-icon size="30" style="opacity:.25">mdi-image-outline</v-icon>
              </div>
            </div>
            <div class="ied-img-field">
              <label class="ied-label">URL de l'image</label>
              <input v-model="ingEditForm.productImage" class="ied-input" placeholder="https://..." />
            </div>
          </div>

          <!-- Identité -->
          <div class="ied-section">
            <p class="ied-section-title">Identité</p>
            <div class="ied-grid-2">
              <div class="ied-field">
                <label class="ied-label">Nom du produit</label>
                <input v-model="ingEditForm.productName" class="ied-input" />
              </div>
              <div class="ied-field">
                <label class="ied-label">Marque</label>
                <input v-model="ingEditForm.productBrand" class="ied-input" placeholder="Facultatif" />
              </div>
            </div>
            <div class="ied-field mt-2">
              <label class="ied-label">Lien produit</label>
              <input v-model="ingEditForm.productLink" class="ied-input" placeholder="https://..." />
            </div>
          </div>

          <!-- Qualité -->
          <div class="ied-section">
            <p class="ied-section-title">Qualité</p>
            <div class="ied-grid-2">
              <div class="ied-field">
                <label class="ied-label">Nutriscore</label>
                <div class="ied-radio-row">
                  <button
                    v-for="s in NUTRISCORE_OPTIONS" :key="s"
                    class="ied-score-btn"
                    :class="{ 'ied-score-btn--active': ingEditForm.nutriscore === s }"
                    :style="ingEditForm.nutriscore === s ? { background: nutriscoreBg(s), color: '#fff', borderColor: nutriscoreBg(s) } : {}"
                    @click="ingEditForm.nutriscore = ingEditForm.nutriscore === s ? '' : s"
                  >{{ s }}</button>
                </div>
              </div>
              <div class="ied-field">
                <label class="ied-label">Nova</label>
                <div class="ied-radio-col">
                  <button
                    v-for="opt in NOVA_OPTIONS" :key="opt.value"
                    class="ied-nova-btn"
                    :class="{ 'ied-nova-btn--active': ingEditForm.novaGroup === opt.value }"
                    @click="ingEditForm.novaGroup = ingEditForm.novaGroup === opt.value ? '' : opt.value"
                  >
                    <span class="ied-nova-dot" :style="{ background: ['#2d9a4e','#c8b400','#e07020','#e63946'][opt.value - 1] }" />
                    {{ opt.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Valeurs nutritionnelles / 100g -->
          <div class="ied-section">
            <p class="ied-section-title">Valeurs nutritionnelles <span class="ied-section-sub">/ 100 g</span></p>
            <div class="ied-grid-3">
              <div class="ied-field">
                <label class="ied-label">Calories (kcal)</label>
                <input v-model="ingEditForm.kcal" class="ied-input" type="number" min="0" />
              </div>
              <div class="ied-field">
                <label class="ied-label">Protéines (g)</label>
                <input v-model="ingEditForm.protein" class="ied-input" type="number" min="0" step="0.1" />
              </div>
              <div class="ied-field">
                <label class="ied-label">Glucides (g)</label>
                <input v-model="ingEditForm.carbs" class="ied-input" type="number" min="0" step="0.1" />
              </div>
              <div class="ied-field">
                <label class="ied-label">Lipides (g)</label>
                <input v-model="ingEditForm.fat" class="ied-input" type="number" min="0" step="0.1" />
              </div>
              <div class="ied-field">
                <label class="ied-label">Fibres (g)</label>
                <input v-model="ingEditForm.fiber" class="ied-input" type="number" min="0" step="0.1" />
              </div>
            </div>
          </div>

          <!-- Magasins -->
          <div class="ied-section">
            <p class="ied-section-title">Disponible dans</p>
            <div class="ied-store-pills">
              <button
                v-for="store in STORES_OPTIONS" :key="store"
                class="ied-store-pill"
                :class="{ 'ied-store-pill--active': ingEditForm.availableStores?.includes(store) }"
                @click="toggleStore(store)"
              >{{ store }}</button>
            </div>
          </div>

        </v-card-text>

        <!-- Footer -->
        <div class="ied-footer">
          <button class="dialog-btn dialog-btn--cancel" @click="ingEditDialog = false">Annuler</button>
          <button class="dialog-btn dialog-btn--save" :disabled="ingEditSaving" @click="saveIngredientEdit">
            <v-progress-circular v-if="ingEditSaving" indeterminate size="14" width="2" class="mr-1" />
            Enregistrer
          </button>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :timeout="2500" rounded="pill">{{ snack.text }}</v-snackbar>
    <v-alert v-if="errorMessage" type="error" variant="tonal" class="mt-4 mx-4">{{ errorMessage }}</v-alert>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useAuthStore } from '@/store/auth_store'
  import { getUserRecipeById, deleteRecipeFromUserCollection, setRecipeAsFavorite } from '@/service/recipe_service'
  import { getBaseRecipeById, deleteBaseRecipe } from '@/service/base_recipe_service'
  import { getIngredientsByBarcodes, getNovaStringAndColor, updateProduct } from '@/service/ingredient_service'
  import { addShoppingList, getUserShoppingLists } from '@/service/shopping_list_service'
  import TopDeck from '@/components/Utils/TopDeck.vue'

  const props = defineProps({ id: { type: String, required: true } })
  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const recipe = ref(null)
  const isLoading = ref(false)
  const errorMessage = ref('')
  const snack = ref({ show: false, text: '' })
  const askDelete = ref(false)

  const ingredientDetailDialog = ref({ open: false, ing: null })
  function openIngredientDetail(ing) {
    ingredientDetailDialog.value = { open: true, ing }
  }

  // ── Édition ingrédient (admin) ──
  const ingEditDialog = ref(false)
  const ingEditSaving = ref(false)
  const ingEditForm = ref({})
  const NOVA_OPTIONS = [
    { value: 1, label: 'Nova 1 — Non transformé' },
    { value: 2, label: 'Nova 2 — Ingrédient culinaire' },
    { value: 3, label: 'Nova 3 — Transformé' },
    { value: 4, label: 'Nova 4 — Ultra-transformé' },
  ]
  const NUTRISCORE_OPTIONS = ['A', 'B', 'C', 'D', 'E']
  const STORES_OPTIONS = ['Carrefour', 'Leclerc', 'Lidl', 'Aldi', 'Intermarché', 'Super U', 'Monoprix', 'Biocoop', 'Casino', 'Autre']

  function openIngredientEdit(ing) {
    ingEditForm.value = {
      id: ing.id,
      productName: ing.productName || ing.name || '',
      productBrand: ing.productBrand || '',
      productImage: ing.productImage || '',
      productLink: ing.productLink || '',
      nutriscore: ing.nutriscore || '',
      novaGroup: getNovaNumber(ing.novaGroup) ?? '',
      availableStores: [...(ing.availableStores || [])],
      // nutriments editables
      kcal: ing.nutritionPer100g?.kcal ?? '',
      protein: ing.nutritionPer100g?.protein ?? '',
      carbs: ing.nutritionPer100g?.carbs ?? '',
      fat: ing.nutritionPer100g?.fat ?? '',
      fiber: ing.nutritionPer100g?.fiber ?? '',
    }
    ingEditDialog.value = true
  }

  async function saveIngredientEdit() {
    if (!authStore.userid || !ingEditForm.value.id) return
    ingEditSaving.value = true
    try {
      const f = ingEditForm.value
      const payload = {
        productName: f.productName,
        productBrand: f.productBrand,
        productImage: f.productImage,
        productLink: f.productLink,
        nutriscore: f.nutriscore || null,
        novaGroup: f.novaGroup !== '' ? Number(f.novaGroup) : null,
        availableStores: f.availableStores,
        nutritionPer100g: {
          kcal: f.kcal !== '' ? Number(f.kcal) : null,
          protein: f.protein !== '' ? Number(f.protein) : null,
          carbs: f.carbs !== '' ? Number(f.carbs) : null,
          fat: f.fat !== '' ? Number(f.fat) : null,
          fiber: f.fiber !== '' ? Number(f.fiber) : null,
        },
      }
      await updateProduct(authStore.userid, { [f.id]: payload })
      // Mise à jour locale immédiate dans recipe.ingredients
      const idx = recipe.value.ingredients.findIndex(i => i.id === f.id)
      if (idx !== -1) {
        recipe.value.ingredients[idx] = {
          ...recipe.value.ingredients[idx],
          productName: f.productName,
          name: f.productName,
          productBrand: f.productBrand,
          productImage: f.productImage,
          productLink: f.productLink,
          nutriscore: f.nutriscore,
          novaGroup: f.novaGroup !== '' ? Number(f.novaGroup) : null,
          availableStores: f.availableStores,
          nutritionPer100g: payload.nutritionPer100g,
        }
      }
      ingEditDialog.value = false
      snack.value = { show: true, text: 'Ingrédient mis à jour ✓' }
    } catch (e) {
      console.error(e)
      snack.value = { show: true, text: 'Erreur lors de la sauvegarde.' }
    } finally {
      ingEditSaving.value = false
    }
  }

  function nutriscoreBg(v) {
    const map = { A: '#2d9a4e', B: '#78c240', C: '#f5c518', D: '#f07823', E: '#e63946' }
    return map[v?.toUpperCase()] || '#9E9E9E'
  }

  const NUTRIMENT_LABELS = {
    'energy-kcal': 'Énergie (kcal)', 'fat': 'Lipides', 'saturated-fat': 'dont AG saturés',
    'carbohydrates': 'Glucides', 'sugar': 'dont Sucres', 'sugars': 'dont Sucres',
    'proteins': 'Protéines', 'fiber': 'Fibres', 'salt': 'Sel',
  }
  function nutrimentLabel(key) { return NUTRIMENT_LABELS[key] || key }

  const isAdmin = computed(() => authStore.userInfos?.role === 'admin')
  const isFromBaseCatalog = computed(() => route.query?.source === 'base')
  const canEditBaseRecipe = computed(() => {
    if (!recipe.value) return false
    if (isFromBaseCatalog.value) return isAdmin.value
    return !recipe.value.baseRecipe || isAdmin.value
  })

  const validPreview = ref(true)
  const placeholderImage = 'https://www.mimisrecipes.com/wp-content/uploads/2018/12/recipe-placeholder-featured.jpg'
  const sourceKey = computed(() => recipe.value?.url || recipe.value?.image || recipe.value?.imageUrl || '')
  const previewSrc = computed(() => validPreview.value && sourceKey.value ? sourceKey.value : placeholderImage)
  function onPreviewError() { validPreview.value = false }

  const novaWarning = computed(() => {
    if (!recipe.value?.ingredients?.length) return null
    const novaGroups = recipe.value.ingredients
      .map(ing => {
        const display = getNovaDisplay(ing.novaGroup)
        if (!display) return null
        const match = display.string.match(/\d+/)
        return match ? parseInt(match[0]) : null
      })
      .filter(n => n !== null)
    if (!novaGroups.length) return null
    const hasNova4 = novaGroups.some(n => n === 4)
    const hasNova3 = novaGroups.some(n => n === 3)
    const nova4Count = novaGroups.filter(n => n === 4).length
    const nova3Count = novaGroups.filter(n => n === 3).length
    if (hasNova4) return { type: 'error', icon: 'mdi-alert-circle', message: `Cette recette contient ${nova4Count} ingrédient(s) ultra-transformé(s) (Nova 4). Consommation à limiter.` }
    if (hasNova3 && nova3Count > 1) return { type: 'warning', icon: 'mdi-alert', message: `Cette recette contient ${nova3Count} ingrédients transformés (Nova 3). À consommer avec modération.` }
    return null
  })

  function getNovaDisplay(novaGroup) {
    if (!novaGroup) return null
    let num = null
    if (typeof novaGroup === 'number') { num = novaGroup }
    else if (typeof novaGroup === 'object') {
      if (novaGroup.string) { const m = novaGroup.string.match(/\d+/); num = m ? parseInt(m[0]) : null }
      else if (typeof novaGroup.value === 'number') { num = novaGroup.value }
    } else if (typeof novaGroup === 'string') { const m = novaGroup.match(/\d+/); num = m ? parseInt(m[0]) : null }
    if (!num || num < 1 || num > 4) return null
    return getNovaStringAndColor(num)
  }

  function getNovaNumber(novaGroup) {
    if (!novaGroup) return null
    let num = null
    if (typeof novaGroup === 'number') { num = novaGroup }
    else if (typeof novaGroup === 'object') {
      if (novaGroup.string) { const m = novaGroup.string.match(/\d+/); num = m ? parseInt(m[0]) : null }
      else if (typeof novaGroup.value === 'number') { num = novaGroup.value }
    } else if (typeof novaGroup === 'string') { const m = novaGroup.match(/\d+/); num = m ? parseInt(m[0]) : null }
    return num
  }

  function format1(n) {
    const v = Number(n ?? 0)
    return Number.isFinite(v) ? v.toFixed(1) : '0.0'
  }

  function macroBarWidth(macro) {
    const totals = recipe.value?.totals
    if (!totals) return '0%'
    const protein = Number(totals.protein ?? 0) * 4
    const carbs = Number(totals.carbs ?? 0) * 4
    const fat = Number(totals.fat ?? 0) * 9
    const total = protein + carbs + fat
    if (!total) return '0%'
    const map = { protein, carbs, fat }
    return Math.round((map[macro] / total) * 100) + '%'
  }

  async function loadRecipe() {
    if (!authStore.userid) return
    try {
      isLoading.value = true
      let r = await getUserRecipeById(authStore.userid, props.id)
      if (!r || r.baseRecipe === true) {
        const base = await getBaseRecipeById(props.id)
        if (base) { r = base }
        else if (!r) { errorMessage.value = 'Recette introuvable.'; return }
      }
      const barcodes = r.ingredients?.map(i => i.id) || []
      const fullInfos = await getIngredientsByBarcodes(authStore.userid, barcodes)
      const hydratedIngredients = (r.ingredients || []).map(line => {
        const found = fullInfos.find(f => f.id === line.id)
        return {
          ...line,
          name: line.name || found?.productName || line.id,
          productName: found?.productName || line.name || line.id,
          productBrand: found?.productBrand || '',
          novaGroup: line.novaGroup ?? found?.novaGroup ?? null,
          nutritionPer100g: found?.nutritionPer100g || {},
          productImage: found?.productImage || line.productImage || '',
          nutriscore: found?.nutriscore || line.nutriscore || '',
          availableStores: found?.availableStores || line.availableStores || [],
          productLink: found?.productLink || line.productLink || '',
          nutriments_map: found?.nutriments_map || line.nutriments_map || [],
        }
      })
      recipe.value = { id: props.id, ...r, ingredients: hydratedIngredients }
    } catch (e) {
      console.error(e)
      errorMessage.value = "Erreur lors du chargement de la recette."
    } finally {
      isLoading.value = false
    }
  }

  async function deleteRecipe() {
    if (!canEditBaseRecipe.value) { errorMessage.value = "Action réservée aux administrateurs."; return }
    if (!authStore.userid || !recipe.value?.id) return
    try {
      if (recipe.value?.baseRecipe) { await deleteBaseRecipe(recipe.value.id) }
      else { await deleteRecipeFromUserCollection(authStore.userid, recipe.value.id) }
      askDelete.value = false
      snack.value = { show: true, text: 'Recette supprimée 🗑️' }
      setTimeout(() => router.push('/my_recipes'), 350)
    } catch (e) { console.error(e); errorMessage.value = "Erreur lors de la suppression." }
  }

  async function toggleFavorite() {
    if (!authStore.userid || !recipe.value?.id) return
    try {
      const next = !recipe.value.favorite
      await setRecipeAsFavorite(authStore.userid, recipe.value.id, next)
      recipe.value.favorite = next
      snack.value = { show: true, text: next ? 'Ajoutée aux favoris ⭐' : 'Retirée des favoris' }
    } catch (e) { console.error(e); errorMessage.value = "Erreur lors de la mise à jour du favori." }
  }

  async function createShoppingListFromRecipe() {
    if (!authStore.userid) { errorMessage.value = "Utilisateur non connecté."; return }
    const ingredients = recipe.value?.ingredients || []
    if (!ingredients.length) { errorMessage.value = "Aucun ingrédient à ajouter."; return }
    const items = ingredients
      .map((ing) => {
        const name = String(ing.name || ing.productName || '').trim()
        if (!name) return null
        const grams = Number(ing.grams)
        const quantity = Number.isFinite(grams) && grams > 0 ? `${grams} g` : 1
        return { name, quantity }
      })
      .filter(Boolean)
    if (!items.length) { errorMessage.value = "Aucun ingrédient valide à ajouter."; return }
    const listName = (recipe.value?.title || "Liste de courses").trim()
    try {
      const existingLists = await getUserShoppingLists(authStore.userid)
      const alreadyGenerated = Object.values(existingLists || {}).some((list) => {
        if (!list) return false
        return String(list.name || "").trim().toLowerCase() === listName.toLowerCase() && list.userGenerated === false
      })
      if (alreadyGenerated) { snack.value = { show: true, text: "Cette liste existe déjà." }; return }
      await addShoppingList(authStore.userid, { name: listName, items, userGenerated: false })
      snack.value = { show: true, text: "Liste de courses créée." }
    } catch (e) { console.error(e); errorMessage.value = "Erreur lors de la création de la liste." }
  }

  function editRecipe() {
    if (!canEditBaseRecipe.value) { errorMessage.value = "Action réservée aux administrateurs."; return }
    if (recipe.value?.baseRecipe) { router.push({ path: '/base_recipes', query: { id: recipe.value.id } }); return }
    router.push({ path: '/add_recipe', query: { id: recipe.value.id } })
  }

  function requestDelete() {
    if (!canEditBaseRecipe.value) { errorMessage.value = "Action réservée aux administrateurs."; return }
    askDelete.value = true
  }

  function toggleStore(store) {
    const list = ingEditForm.value.availableStores
    const idx = list.indexOf(store)
    if (idx === -1) list.push(store)
    else list.splice(idx, 1)
  }

  onMounted(() => {
    watch(() => authStore.userid, () => loadRecipe(), { immediate: true })
  })
  watch(sourceKey, (v) => { validPreview.value = !!v })
</script>

<style scoped>
/* ════════════════════════════════════════════════
   PAGE
════════════════════════════════════════════════ */
.recipe-page { min-height: 100vh; }
.recipe-topbar { padding: 20px 24px 12px; }
.detail-text { color: rgb(var(--v-theme-text)); }

/* ════════════════════════════════════════════════
   LAYOUT CENTRÉ
════════════════════════════════════════════════ */
.recipe-centered {
  max-width: 820px;
  margin: 0 auto;
  padding: 0 32px 60px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

/* Eyebrow */
.hero-eyebrow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.hero-eyebrow-text {
  font-size: 11px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.35);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.hero-title {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: rgb(var(--v-theme-text));
  margin: 0 0 24px;
  width: 100%;
  letter-spacing: -0.3px;
}

/* ════════════════════════════════════════════════
   HERO LAYOUT : image gauche + macros droite
════════════════════════════════════════════════ */
.hero-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  width: 100%;
  margin-bottom: 36px;
  align-items: start;
}

/* ── Colonne gauche ── */
.hero-left { display: flex; flex-direction: column; gap: 14px; }

.hero-img-frame {
  width: 100%;
  border-radius: 18px;
  overflow: hidden;
  aspect-ratio: 4/3;
  background: rgba(var(--v-theme-text), 0.05);
}
.hero-img { width: 100%; height: 100%; }

/* Tags */
.hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tag-pill {
  font-size: 11.5px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(var(--v-theme-text), 0.06);
  color: rgba(var(--v-theme-text), 0.6);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  line-height: 1.4;
}
.tag-pill--accent {
  background: rgba(var(--v-theme-accent), 0.1);
  color: rgb(var(--v-theme-accent));
  border-color: rgba(var(--v-theme-accent), 0.15);
  font-weight: 600;
}

/* ── Colonne droite : macros ── */
.hero-right {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 4px;
}

.macro-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.macro-kcal {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.macro-kcal__val {
  font-size: 38px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  line-height: 1;
  letter-spacing: -1px;
}
.macro-kcal__unit {
  font-size: 14px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.4);
}
.macro-portions {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.45);
  background: rgba(var(--v-theme-text), 0.05);
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
}

.macro-bars { display: flex; flex-direction: column; gap: 10px; }
.macro-bar-item { display: flex; flex-direction: column; gap: 5px; }
.macro-bar-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.macro-bar-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.55);
}
.macro-bar-val {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}
.macro-bar-track {
  height: 5px;
  border-radius: 999px;
  background: rgba(var(--v-theme-text), 0.07);
  overflow: hidden;
}
.macro-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.5s ease;
}
.macro-bar-fill--protein { background: #7553FC; }
.macro-bar-fill--carbs   { background: #F4A642; }
.macro-bar-fill--fat     { background: #5EC4A0; }

.macro-per-portion {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.45);
}

/* Warning nova */
.recipe-warning {
  display: flex;
  align-items: flex-start;
  font-size: 11.5px;
  font-weight: 500;
  padding: 9px 12px;
  border-radius: 10px;
  line-height: 1.5;
  gap: 6px;
}
.recipe-warning--error {
  background: rgba(230,57,70,0.07);
  color: #e63946;
  border: 1px solid rgba(230,57,70,0.15);
}
.recipe-warning--warning {
  background: rgba(240,120,35,0.07);
  color: #e07020;
  border: 1px solid rgba(240,120,35,0.15);
}

/* Actions */
.hero-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 12px;
  border: 1.5px solid rgba(var(--v-theme-text), 0.12);
  background: transparent;
  color: rgb(var(--v-theme-text));
  cursor: pointer;
  transition: all 0.16s ease;
  width: 100%;
  letter-spacing: 0.1px;
}
.action-btn:hover {
  background: rgba(var(--v-theme-text), 0.05);
  border-color: rgba(var(--v-theme-text), 0.2);
}
.action-btn--cart {
  border-color: rgba(var(--v-theme-accent), 0.3);
  color: rgb(var(--v-theme-accent));
  background: rgba(var(--v-theme-accent), 0.05);
}
.action-btn--cart:hover {
  background: rgba(var(--v-theme-accent), 0.1);
  border-color: rgba(var(--v-theme-accent), 0.4);
}

.action-icons {
  display: flex;
  gap: 8px;
}

.hero-btn {
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  border-radius: 999px;
  transition: all 0.16s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.hero-btn--icon {
  width: 36px;
  height: 36px;
  background: rgba(var(--v-theme-text), 0.05);
  color: rgba(var(--v-theme-text), 0.5);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
}
.hero-btn--icon:hover { background: rgba(var(--v-theme-text), 0.09); }
.hero-btn--icon-active {
  background: rgba(255,193,7,0.12);
  color: #FFC107;
  border-color: rgba(255,193,7,0.25);
}
.hero-btn--danger:hover {
  background: rgba(230,57,70,0.08);
  color: #e63946;
  border-color: rgba(230,57,70,0.15);
}

/* ════════════════════════════════════════════════
   SECTIONS
════════════════════════════════════════════════ */
.section-block {
  width: 100%;
  margin-bottom: 36px;
}

.section-title {
  font-size: 19px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: -0.2px;
}
.section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  background: rgba(var(--v-theme-accent), 0.10);
  color: rgb(var(--v-theme-accent));
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

/* ── Bulles ingrédients ── */
.ing-bubbles {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 12px;
  justify-content: flex-start;
}

.ing-bubble {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 88px;
}
.ing-bubble--clickable { cursor: pointer; }
.ing-bubble--clickable:hover .ing-bubble__circle {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
}
.ing-bubble__circle {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(var(--v-theme-text), 0.05);
  border: 1.5px solid rgba(var(--v-theme-text), 0.09);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  flex-shrink: 0;
}
.ing-bubble__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.ing-bubble__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Badges sur la bulle */
.ing-card__badges {
  position: absolute;
  top: 4px;
  right: 2px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-end;
}
.ing-micro-badge {
  font-size: 8px;
  font-weight: 800;
  color: #fff;
  border-radius: 4px;
  padding: 1px 3px;
  letter-spacing: 0.2px;
}

.ing-bubble__name {
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--v-theme-text));
  text-align: center;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
}
.ing-bubble__qty {
  font-size: 11px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.4);
  text-align: center;
}

/* Empty state */
.recipe-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px;
  font-size: 13px;
  color: rgba(var(--v-theme-text), 0.4);
  background: rgba(var(--v-theme-tertiary), 1);
  border: 1px solid rgba(var(--v-theme-text), 0.07);
  border-radius: 16px;
  width: 100%;
}

/* ── Étapes ── */
.steps-list { display: flex; flex-direction: column; gap: 14px; }
.step-item { display: flex; gap: 14px; align-items: flex-start; }
.step-number {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(var(--v-theme-accent), 0.08);
  color: rgb(var(--v-theme-accent));
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}
.step-content { flex: 1; font-size: 14px; line-height: 1.7; padding-top: 2px; }
.step-content :deep(p) { margin: 0 0 0.3em; }
.step-content :deep(strong) { font-weight: 700; }

/* Crayon admin sur la bulle */
.ing-bubble__edit {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255,255,255,0.92);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #7553FC;
  box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  transition: transform 0.15s, box-shadow 0.15s;
  z-index: 3;
}
.ing-bubble__edit:hover {
  transform: translateX(-50%) scale(1.12);
  box-shadow: 0 3px 8px rgba(0,0,0,0.2);
}

/* ════════════════════════════════════════════════
   DIALOG ÉDITION INGRÉDIENT
════════════════════════════════════════════════ */
.ied-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid rgba(var(--v-theme-text), 0.07);
}
.ied-header__label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(var(--v-theme-text), 0.4);
  margin: 0 0 3px;
}
.ied-header__name {
  font-size: 16px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  margin: 0;
}

.ied-body { display: flex; flex-direction: column; gap: 20px; padding: 20px !important; }

.ied-img-preview {
  display: flex;
  gap: 14px;
  align-items: center;
}
.ied-img-wrap {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(var(--v-theme-text), 0.05);
  border: 1.5px solid rgba(var(--v-theme-text), 0.09);
}
.ied-img { width: 100%; height: 100%; object-fit: cover; }
.ied-img-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.ied-img-field { flex: 1; }

.ied-section { display: flex; flex-direction: column; gap: 10px; }
.ied-section-title {
  font-size: 12px;
  font-weight: 600;
  color: rgba(var(--v-theme-text), 0.5);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0;
}
.ied-section-sub { font-weight: 400; text-transform: none; letter-spacing: 0; }

.ied-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.ied-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
.ied-field { display: flex; flex-direction: column; gap: 5px; }
.mt-2 { margin-top: 4px; }

.ied-label {
  font-size: 11.5px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.5);
}
.ied-input {
  width: 100%;
  font-family: inherit;
  font-size: 13px;
  font-weight: 400;
  color: rgb(var(--v-theme-text));
  background: rgba(var(--v-theme-text), 0.04);
  border: 1px solid rgba(var(--v-theme-text), 0.1);
  border-radius: 10px;
  padding: 8px 11px;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.ied-input:focus { border-color: rgb(var(--v-theme-accent)); }
.ied-input[type=number] { -moz-appearance: textfield; }
.ied-input[type=number]::-webkit-outer-spin-button,
.ied-input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }

/* Nutriscore */
.ied-radio-row { display: flex; gap: 6px; flex-wrap: wrap; }
.ied-score-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1.5px solid rgba(var(--v-theme-text), 0.12);
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  color: rgba(var(--v-theme-text), 0.5);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.14s;
}
.ied-score-btn:hover { border-color: rgba(var(--v-theme-text), 0.25); }

/* Nova */
.ied-radio-col { display: flex; flex-direction: column; gap: 5px; }
.ied-nova-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 400;
  color: rgba(var(--v-theme-text), 0.6);
  background: transparent;
  border: 1px solid rgba(var(--v-theme-text), 0.1);
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: all 0.14s;
}
.ied-nova-btn:hover { background: rgba(var(--v-theme-text), 0.04); }
.ied-nova-btn--active {
  background: rgba(var(--v-theme-accent), 0.07);
  border-color: rgba(var(--v-theme-accent), 0.3);
  color: rgb(var(--v-theme-text));
  font-weight: 500;
}
.ied-nova-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Magasins */
.ied-store-pills { display: flex; flex-wrap: wrap; gap: 6px; }
.ied-store-pill {
  font-size: 12px;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(var(--v-theme-text), 0.12);
  background: transparent;
  color: rgba(var(--v-theme-text), 0.55);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.14s;
}
.ied-store-pill:hover { background: rgba(var(--v-theme-text), 0.05); }
.ied-store-pill--active {
  background: rgba(var(--v-theme-accent), 0.1);
  border-color: rgba(var(--v-theme-accent), 0.3);
  color: rgb(var(--v-theme-accent));
}

/* Footer dialog */
.ied-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px 20px;
  border-top: 1px solid rgba(var(--v-theme-text), 0.07);
}
.dialog-btn--save {
  background: rgb(var(--v-theme-accent));
  color: #fff;
  display: inline-flex;
  align-items: center;
}
.dialog-btn--save:disabled { opacity: 0.6; cursor: not-allowed; }

/* ════════════════════════════════════════════════
   DIALOG INGRÉDIENT
════════════════════════════════════════════════ */
.ing-detail-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 20px 14px;
  border-bottom: 1px solid rgba(var(--v-theme-text), 0.07);
}
.ing-detail-img-wrap {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(var(--v-theme-text), 0.05);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
}
.ing-detail-img { width: 100%; height: 100%; object-fit: cover; }
.ing-detail-img-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.ing-detail-info { flex: 1; min-width: 0; }
.ing-detail-name { font-size: 15px; font-weight: 700; color: rgb(var(--v-theme-text)); margin: 0 0 2px; line-height: 1.3; }
.ing-detail-brand { font-size: 12px; color: rgba(var(--v-theme-text), 0.5); margin: 0; }
.ing-detail-badges { display: flex; gap: 4px; margin-top: 6px; flex-wrap: wrap; }
.ing-detail-close {
  flex-shrink: 0; width: 28px; height: 28px; border-radius: 50%;
  background: rgba(var(--v-theme-text), 0.07); border: none;
  color: rgba(var(--v-theme-text), 0.6); display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.ing-detail-close:hover { background: rgba(var(--v-theme-text), 0.12); }

.ing-product-link {
  display: inline-flex; align-items: center; font-size: 13px; font-weight: 500;
  color: rgb(var(--v-theme-accent)); text-decoration: none;
}
.ing-product-link:hover { text-decoration: underline; }

.nutr-table { background: rgba(var(--v-theme-text), 0.03); border-radius: 10px; overflow: hidden; }
.nutr-table-row {
  display: flex; justify-content: space-between; font-size: 13px;
  padding: 7px 12px; border-bottom: 1px solid rgba(var(--v-theme-text), 0.05);
}
.nutr-table-row:last-child { border-bottom: none; }

.store-pill {
  display: inline-flex; align-items: center; font-size: 12px; font-weight: 500;
  padding: 4px 10px; border-radius: 999px;
  background: rgba(var(--v-theme-accent), 0.1); color: rgb(var(--v-theme-accent));
}

/* ════════════════════════════════════════════════
   DIALOG SUPPRESSION
════════════════════════════════════════════════ */
.dialog-btn {
  font-size: 13px; font-weight: 600; padding: 8px 18px;
  border-radius: 999px; border: none; cursor: pointer; transition: all 0.15s;
}
.dialog-btn--cancel {
  background: rgba(var(--v-theme-text), 0.07);
  color: rgba(var(--v-theme-text), 0.7);
  margin-right: 8px;
}
.dialog-btn--cancel:hover { background: rgba(var(--v-theme-text), 0.12); }
.dialog-btn--delete { background: #e63946; color: #fff; }
.dialog-btn--delete:hover { background: #c1121f; }

/* ════════════════════════════════════════════════
   RESPONSIVE
════════════════════════════════════════════════ */
@media (max-width: 700px) {
  .recipe-centered { padding: 0 20px 48px; }
  .hero-layout { grid-template-columns: 1fr; gap: 20px; }
  .hero-title { font-size: 22px; }
  .macro-kcal__val { font-size: 30px; }
  .action-btn { width: 100%; }
}
@media (max-width: 500px) {
  .recipe-centered { padding: 0 16px 40px; }
  .ing-bubbles { gap: 14px 10px; }
  .ing-bubble { width: 76px; }
  .ing-bubble__circle { width: 68px; height: 68px; }
}
</style>
